import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import styled, { css } from 'styled-components';

import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { IgProfile } from '@/types/resources';
import { api } from '@/utils/api';

import Form from './Form';

const Tr = styled.tr<{ $isInvalid: boolean }>`
  ${({ $isInvalid, theme }) =>
    $isInvalid &&
    css`
      > td {
        background: ${theme.colors.errorLight};
      }
    `}
`;

interface Props {
  profile: IgProfile;
}

const TableRow: FC<Props> = ({ profile }) => {
  const [showEditModal, setShowEditModal] = useState(false);

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDelete = async () => {
    try {
      const confirm = await alerts.confirm(
        `Are you sure you want to delete this profile?`,
        profile.username,
      );

      if (!confirm) return;

      await api.delete(`/v1/ig-profiles-tracker/profiles/${profile.id}`);

      await queryClient.invalidateQueries(['ig_trackable_profiles']);

      alerts.success('Profile was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  return (
    <Tr $isInvalid={profile.isInvalid}>
      <Td>
        <Text
          as="a"
          $type="smallBold"
          target="_blank"
          href={`https://instagram.com/${profile.username}`}
          rel="noreferrer noopener"
        >
          {profile.username}
        </Text>
      </Td>
      <Td>{profile.tierLabel}</Td>
      <Td>{profile.postsCount}</Td>
      <Td>{DateTime.fromISO(profile.createdAt).toFormat('yyyy/LL/dd')}</Td>
      <Td $actionsCol>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton
              as="button"
              onClick={() => setShowEditModal(true)}
            >
              <span>
                <PencilIcon />
              </span>
              Edit
            </ActionsDropdownButton>
            <ActionsDropdownButton as="button" onClick={onDelete}>
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
        <AnimatePresence>
          {showEditModal && (
            <Form
              editableProfile={profile}
              onClose={() => setShowEditModal(false)}
            />
          )}
        </AnimatePresence>
      </Td>
    </Tr>
  );
};

export default TableRow;
